"use client";

import React from "react";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
  PaginationButton,
} from "@/components/ui/pagination";
import { useRouter } from "@/i18n/routing";

type Props = {
  currentPage: number;
  totalPages: number;
  perPage: number;
  onPageChange?: (page: number) => void;
};

const BasicPagination: React.FC<Props> = ({
  currentPage,
  totalPages,
  onPageChange,
}) => {
  const router = useRouter();
  const generatePagination = () => {
    const pages: (number | string)[] = [];

    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      if (start > 2) {
        pages.push("...");
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (end < totalPages - 1) {
        pages.push("...");
      }

      pages.push(totalPages);
    }

    return pages;
  };

  const pages = generatePagination();

  if (totalPages <= 1) {
    return <div />;
  }

  const onPageUpdate = (page: number) => {
    if (page < 1 || page > totalPages) {
      return;
    }

    if (onPageChange) {
      onPageChange(page);
    } else {
      const params = new URLSearchParams(window.location.search || "");

      if (page > 1) {
        params.set("page", page.toString());
      } else {
        params.delete("page");
      }

      const baseUrl = window.location.pathname;
      const finalUrl =
        baseUrl + (params.toString() ? `?${params.toString()}` : "");

      router.push(finalUrl);
    }
  };

  return (
    <Pagination>
      <PaginationContent>
        {currentPage > 1 && (
          <PaginationItem>
            <PaginationPrevious onClick={() => onPageUpdate(currentPage - 1)} />
          </PaginationItem>
        )}

        {pages.map((page, index) =>
          page === "..." ? (
            <PaginationItem key={index}>
              <PaginationEllipsis />
            </PaginationItem>
          ) : (
            <PaginationItem key={index}>
              <PaginationButton
                isActive={currentPage === (page as number)}
                onClick={() => onPageUpdate(page as number)}
              >
                {page}
              </PaginationButton>
            </PaginationItem>
          )
        )}

        {currentPage < totalPages && (
          <PaginationItem>
            <PaginationNext onClick={() => onPageUpdate(currentPage + 1)} />
          </PaginationItem>
        )}
      </PaginationContent>
    </Pagination>
  );
};

export default BasicPagination;
